import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GitHubIcon from "@mui/icons-material/GitHub";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TerminalIcon from "@mui/icons-material/Terminal";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Link,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";

interface CLITabProps {
  maybeApiKey?: string;
}

const CLAUDE_MD_SNIPPET = `# qa-use — E2E Testing CLI

AI-powered E2E testing from your terminal and AI coding agents.

## Commands

\`\`\`bash
qa-use --help                   # List all commands
qa-use <command> --help         # Command-specific help
qa-use docs                     # Full documentation
qa-use docs <topic>             # Topic: browser-commands, test-format, ci, localhost-testing
\`\`\`

## Common Workflows

\`\`\`bash
qa-use test run <name>          # Run a test
qa-use test run --all           # Run all tests
qa-use test list                # List local tests
qa-use browser create           # Start browser session
qa-use browser snapshot         # Get element refs (run before interacting)
qa-use browser click <ref>      # Click an element
qa-use browser close            # Close session
\`\`\`
`;

function maskApiKey(key: string): string {
  if (key.length <= 12) return key;
  return `${key.slice(0, 7)}${"*".repeat(8)}${key.slice(-4)}`;
}

const codeBlockSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.5,
  py: 1,
  backgroundColor: "background.default",
  borderRadius: 1,
  border: "1px solid",
  borderColor: "divider",
  fontFamily: "monospace",
  fontSize: 13,
} as const;

export const CLITab: React.FC<CLITabProps> = ({ maybeApiKey }) => {
  const { enqueueSnackbar } = useSnackbar();

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar(`${label} copied to clipboard`, {
      variant: "success",
      anchorOrigin: { horizontal: "right", vertical: "bottom" },
    });
  };

  const installGlobalCommand = "npm install -g @desplega.ai/qa-use";
  const npxCommand = "npx @desplega.ai/qa-use";
  const bunxCommand = "bunx @desplega.ai/qa-use";
  const configCommand = maybeApiKey ? `qa-use setup --api-key ${maybeApiKey}` : "qa-use setup";
  const maskedConfigCommand = maybeApiKey
    ? `qa-use setup --api-key ${maskApiKey(maybeApiKey)}`
    : "qa-use setup";
  const skillInstallCommand = "npx skills add https://github.com/desplega-ai/qa-use --skill qa-use";
  const claudeMarketplaceCommand = "claude plugin marketplace add desplega-ai/qa-use";
  const claudeInstallCommand = "claude plugin install qa-use@desplega.ai";

  const llmOnboardingPrompt = [
    "Set up qa-use for E2E testing in this project. Follow these steps:",
    "",
    "1. Install the CLI globally:",
    `   ${installGlobalCommand}`,
    "",
    "2. Install browser dependencies (Chromium):",
    "   qa-use install-deps",
    "",
    `3. Configure the API key:`,
    `   ${configCommand}`,
    "",
    "4. Install the qa-use skill:",
    `   ${skillInstallCommand}`,
    "",
    "5. Initialize the test directory:",
    "   qa-use test init",
    "",
    "After setup, verify with: qa-use info",
    "For full docs run: qa-use docs",
  ].join("\n");

  return (
    <Stack spacing={2}>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1.5,
              backgroundColor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TerminalIcon sx={{ fontSize: 24, color: "white" }} />
          </Box>
          <Stack spacing={0}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              qa-use CLI
            </Typography>
            <Typography variant="body2" color="text.secondary">
              AI-powered E2E testing from your terminal and AI coding agents
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="GitHub">
            <IconButton
              component="a"
              href="https://github.com/desplega-ai/qa-use"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            >
              <GitHubIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="npm">
            <IconButton
              component="a"
              href="https://www.npmjs.com/package/@desplega.ai/qa-use"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Setup Steps */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Get Started</Typography>
            <Tooltip title="Copy all steps as a prompt for your AI coding agent">
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={() => handleCopy(llmOnboardingPrompt, "LLM onboarding prompt")}
              >
                Copy for LLM
              </Button>
            </Tooltip>
          </Stack>
          <Stepper orientation="vertical" nonLinear>
            {/* Step 1: Install */}
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1" fontWeight={600}>
                  Install the CLI
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  Install qa-use globally:
                </Typography>
                <Box sx={codeBlockSx}>
                  <code style={{ flex: 1, wordBreak: "break-all" }}>{installGlobalCommand}</code>
                  <Tooltip title="Copy">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(installGlobalCommand, "Install command")}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary" mt={1.5} mb={1}>
                  Or run without installing:
                </Typography>
                <Stack spacing={0.75}>
                  <Box sx={codeBlockSx}>
                    <code style={{ flex: 1, wordBreak: "break-all" }}>{npxCommand}</code>
                    <Tooltip title="Copy">
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(npxCommand, "npx command")}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={codeBlockSx}>
                    <code style={{ flex: 1, wordBreak: "break-all" }}>{bunxCommand}</code>
                    <Tooltip title="Copy">
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(bunxCommand, "bunx command")}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary" mt={1.5} mb={1}>
                  Then install the browser (Chromium) for browser commands:
                </Typography>
                <Box sx={codeBlockSx}>
                  <code style={{ flex: 1, wordBreak: "break-all" }}>qa-use install-deps</code>
                  <Tooltip title="Copy">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy("qa-use install-deps", "Install deps command")}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StepContent>
            </Step>

            {/* Step 2: Configure API Key */}
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1" fontWeight={600}>
                  Configure your API key
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  Run the setup command to connect qa-use to your Desplega account:
                </Typography>
                {!maybeApiKey && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    You need an API key first. Go to the <strong>API</strong> tab to create one.
                  </Alert>
                )}
                <Box sx={codeBlockSx}>
                  <code style={{ flex: 1, wordBreak: "break-all" }}>{maskedConfigCommand}</code>
                  <Tooltip title="Copy (includes full API key)">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(configCommand, "Config command")}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                {maybeApiKey && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopy(maybeApiKey, "API key")}
                    sx={{ mt: 1 }}
                  >
                    Copy API key
                  </Button>
                )}
              </StepContent>
            </Step>

            {/* Step 3: Install skill for AI agents */}
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1" fontWeight={600}>
                  Install the skill for AI coding agents
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  optional
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  Add qa-use slash commands to Claude Code and other AI agents:
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                  Via skills.sh (works with any AI agent):
                </Typography>
                <Box sx={codeBlockSx}>
                  <code style={{ flex: 1, wordBreak: "break-all" }}>{skillInstallCommand}</code>
                  <Tooltip title="Copy">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(skillInstallCommand, "Skill install command")}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={1.5}
                  mb={0.75}
                >
                  Or via Claude Code native plugin:
                </Typography>
                <Stack spacing={0.75}>
                  <Box sx={codeBlockSx}>
                    <code style={{ flex: 1, wordBreak: "break-all" }}>
                      {claudeMarketplaceCommand}
                    </code>
                    <Tooltip title="Copy">
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(claudeMarketplaceCommand, "Marketplace command")}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={codeBlockSx}>
                    <code style={{ flex: 1, wordBreak: "break-all" }}>{claudeInstallCommand}</code>
                    <Tooltip title="Copy">
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(claudeInstallCommand, "Plugin install command")}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} mt={1.5} alignItems="center" flexWrap="wrap">
                  <Chip label="/qa-use:verify" size="small" variant="outlined" />
                  <Chip label="/qa-use:explore" size="small" variant="outlined" />
                  <Chip label="/qa-use:test-run" size="small" variant="outlined" />
                  <Link
                    href="https://skills.sh/desplega-ai/qa-use/qa-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    sx={{ ml: 0.5 }}
                  >
                    View on skills.sh
                  </Link>
                </Stack>
              </StepContent>
            </Step>

            {/* Step 4: CLAUDE.md / agents.md Reference */}
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1" fontWeight={600}>
                  Add a CLAUDE.md / agents.md reference
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  optional
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  Add this to your project's <code>CLAUDE.md</code> or <code>agents.md</code> so any
                  AI coding agent knows how to use qa-use:
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.default",
                  }}
                >
                  <Tooltip title="Copy snippet">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(CLAUDE_MD_SNIPPET, "CLAUDE.md snippet")}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                        "&:hover": { backgroundColor: "grey.100" },
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <pre
                    style={{
                      margin: 0,
                      padding: "12px 48px 12px 12px",
                      fontSize: 13,
                      whiteSpace: "pre-wrap",
                      fontFamily: "monospace",
                      lineHeight: 1.5,
                    }}
                  >
                    {CLAUDE_MD_SNIPPET}
                  </pre>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </CardContent>
      </Card>
    </Stack>
  );
};
